import { handleActions } from 'redux-actions'
import { selectorForSlice, unsetState, setState } from 'lp-redux-utils'
import { setOnSuccess, handleSuccess } from 'lp-redux-api'
import * as apiActions from 'api-actions'
import { createSelector } from 'reselect'
import { uniq, map, compact } from 'lodash'
import * as actions from './actions'
import { set } from 'lodash/fp'

const reducerKey = 'product'
const slice = 'root.product'

const initialState = {
  shouldTriggerAllServicesAgreementsFetch: true,
  shouldTriggerServicesAgreementDetailsFetch: true,
  searchShouldTriggerAllServicesAgreementsFetch: false,
  shouldTriggerAllNotificationsPlanAgreementsFetch: true,
  shouldTriggerSearchesFetch: true,
  shouldTriggerAllBDPlanAgreementsFetch: true,
  bdPlanAgreementDetailsId: null,
  shouldTriggerAllTaxableServicesAgreementsFetch: true,
}

const reducer = handleActions(
  {
    [apiActions.fetchSearches]: setOnSuccess('searches'),
    [apiActions.fetchOrderDetails]: setOnSuccess('searchOrder'),
    [apiActions.fetchServicesAgreements]: setOnSuccess('servicesAgreements'),
    [apiActions.fetchServicesAgreementDetails]:
      setOnSuccess('servicesAgreement'),
    [actions.clearServicesAgreementDetails]: unsetState('servicesAgreement'),
    [actions.setShouldTriggerAllServicesAgreementsFetch]: setState(
      'shouldTriggerAllServicesAgreementsFetch'
    ),
    [actions.setShouldTriggerServicesAgreementDetailsFetch]: setState(
      'shouldTriggerServicesAgreementDetailsFetch'
    ),
    [apiActions.fetchBdPlanAgreements]: setOnSuccess('bdPlanAgreements'),
    [actions.setShouldTriggerAllBDPlanAgreementsFetch]: setState(
      'shouldTriggerAllBDPlanAgreementsFetch'
    ),
    [actions.setBdPlanAgreementDetails]: setState('bdPlanAgreement'),
    [actions.clearBDplanAgreementDetails]: unsetState('bdPlanAgreement'),
    [apiActions.fetchNotificationsPlanAgreements]: setOnSuccess(
      'notificationsPlanAgreements'
    ),
    [actions.setShouldTriggerAllNotificationsPlanAgreementsFetch]: setState(
      'shouldTriggerAllNotificationsPlanAgreementsFetch'
    ),
    [apiActions.fetchAvailableInitialInvestments]: setOnSuccess(
      'availableInitialInvestments'
    ),
    [apiActions.fetchServicesAgreementParticipants]: handleSuccess(
      (state, action) => {
        const { servicesAgreementId, participants } = action.payload.data
        return set(
          `servicesAgreementParticipants.${servicesAgreementId}`,
          participants,
          state
        )
      }
    ),
    [apiActions.fetchBenefitDistributionOrders]: handleSuccess(
      (state, action) => {
        const { servicesAgreementId, orders } = action.payload.data
        return set(
          `benefitDistributionOrders.${servicesAgreementId}`,
          orders,
          state
        )
      }
    ),
    [apiActions.fetchNotificationServicesAgreementParticipants]: handleSuccess(
      (state, action) => {
        const { servicesAgreementId, participants } = action.payload.data
        return set(
          `notificationServicesAgreementParticipants.${servicesAgreementId}`,
          participants,
          state
        )
      }
    ),
    [apiActions.fetchSearchServices]: setOnSuccess('searchServices'),
    [apiActions.generateSearchOrderCost]: setOnSuccess('searchOrderCost'),
    [actions.resetSearchOrderCost]: unsetState('searchOrderCost'),
    [actions.setSearchShouldTriggerAllServicesAgreementsFetch]: setState(
      'searchShouldTriggerAllServicesAgreementsFetch'
    ),
    [actions.setShouldTriggerSearchesFetch]: setState(
      'shouldTriggerSearchesFetch'
    ),
    [actions.setBDPlanAgrementDetailsId]: setState('bdPlanAgreementDetailsId'),
    [apiActions.fetchServicesAgreementsTaxableAccounts]: setOnSuccess(
      'servicesAgreementsTaxableAccounts'
    ),
    [actions.setShouldTriggerAllTaxableServicesAgreementsFetch]: setState(
      'shouldTriggerAllTaxableServicesAgreementsFetch'
    ),
    [apiActions.fetchServicesAgreementDetailsTaxableAccounts]: setOnSuccess(
      'servicesAgreementTaxableAccounts'
    ),
  },
  initialState
)

const select = selectorForSlice(slice)

const selectors = {
  searches: select('searches'),
  searchOrder: select('searchOrder'),
  servicesAgreements: select('servicesAgreements'),
  servicesAgreement: select('servicesAgreement'),
  shouldTriggerAllServicesAgreementsFetch: select(
    'shouldTriggerAllServicesAgreementsFetch'
  ),
  shouldTriggerServicesAgreementDetailsFetch: select(
    'shouldTriggerServicesAgreementDetailsFetch'
  ),
  bdPlanAgreements: select('bdPlanAgreements'),

  shouldTriggerAllBDPlanAgreementsFetch: select(
    'shouldTriggerAllBDPlanAgreementsFetch'
  ),
  notificationsPlanAgreements: select('notificationsPlanAgreements'),
  shouldTriggerAllNotificationsPlanAgreementsFetch: select(
    'shouldTriggerAllNotificationsPlanAgreementsFetch'
  ),
  availableInitialInvestments: select('availableInitialInvestments'),
  servicesAgreementParticipants: (state, props) =>
    select(`servicesAgreementParticipants.${props.servicesAgreementId}`)(state),
  benefitDistributionOrders: (state, props) =>
    select(`benefitDistributionOrders.${props.servicesAgreementId}`)(state),
  notificationServicesAgreementParticipants: (state, props) =>
    select(
      `notificationServicesAgreementParticipants.${props.servicesAgreementId}`
    )(state),
  searchServices: select('searchServices'),
  searchOrderCost: select('searchOrderCost'),
  searchShouldTriggerAllServicesAgreementsFetch: select(
    'searchShouldTriggerAllServicesAgreementsFetch'
  ),
  shouldTriggerSearchesFetch: select('shouldTriggerSearchesFetch'),
  //bdPlanAgreement: select('bdPlanAgreement'),
  bdPlanAgreementDetailsId: select('bdPlanAgreementDetailsId'),
  servicesAgreementsTaxableAccounts: select(
    'servicesAgreementsTaxableAccounts'
  ),
  shouldTriggerAllTaxableServicesAgreementsFetch: select(
    'shouldTriggerAllTaxableServicesAgreementsFetch'
  ),
  servicesAgreementTaxableAccounts: select('servicesAgreementTaxableAccounts'),
}

selectors.bdPlanAgreement = createSelector(
  [selectors.bdPlanAgreements, selectors.bdPlanAgreementDetailsId],
  (bdPlanAgreements, bdPlanAgreementDetailsId) => {
    if (!bdPlanAgreements || !bdPlanAgreementDetailsId) return
    const selectedBDPlanAgreement = bdPlanAgreements.find(
      (bdPlanAgreement) =>
        bdPlanAgreement.serviceAgreementId === bdPlanAgreementDetailsId
    )

    return selectedBDPlanAgreement
  }
)

selectors.planFiduciaries = createSelector([selectors.searches], (searches) => {
  if (!searches) return
  const planFiduciaryList = compact(map(searches, 'planFiduciary'))
  return uniq(planFiduciaryList).sort()
})

export { reducer, selectors, reducerKey }
